/**
 * Avatar helpers powered by DiceBear (https://www.dicebear.com/).
 * Free, no API key, no install — just a URL with seed + style.
 */
import { BRAND_LOGOS } from "@/lib/brandLogos";

// Styles ordered: 3D-like (with clothing/torso) first, then iconic styles.
export const AVATAR_STYLES = [
  { id: "personas", label: "Persona 3D", is3d: true },
  { id: "open-peeps", label: "Peeps 3D", is3d: true },
  { id: "notionists", label: "Notion 3D", is3d: true },
  { id: "lorelei", label: "Lorelei" },
  { id: "avataaars", label: "Cartoon" },
  { id: "fun-emoji", label: "Emoji" },
  { id: "big-smile", label: "Big Smile" },
  { id: "bottts", label: "Bot" },
  { id: "adventurer", label: "Adventurer" },
  { id: "micah", label: "Micah" },
];

export const AVATAR_BACKGROUNDS = [
  { id: "ccff00", hex: "#CCFF00", label: "Lima" },
  { id: "00d4ff", hex: "#00D4FF", label: "Cian" },
  { id: "ff6b00", hex: "#FF6B00", label: "Naranja" },
  { id: "ff0080", hex: "#FF0080", label: "Magenta" },
  { id: "8b00ff", hex: "#8B00FF", label: "Púrpura" },
  { id: "ffd700", hex: "#FFD700", label: "Oro" },
  { id: "1a1a1a", hex: "#1A1A1A", label: "Negro" },
  { id: "ffffff", hex: "#FFFFFF", label: "Blanco" },
];

// Padel sponsor brands. Real-world brand colors used as accents.
// `logo` URL points to public-hosted brand artwork. When present, the badge renders the logo instead of the short code.
export const AVATAR_BRANDS = [
  { id: "dyd", name: "DYD Padel", short: "DYD", primary: "#CCFF00", accent: "#000000" },
  { id: "club_premier", name: "Club Premier", short: "CP", primary: "#0D1B4C", accent: "#FFD700" },
  { id: "los_encinos", name: "Los Encinos", short: "ENC", primary: "#1B5E20", accent: "#FFFFFF" },
  { id: "padelville", name: "Padelville", short: "PV", primary: "#7C4DFF", accent: "#FFFFFF" },
  { id: "padel_center", name: "Padel Center", short: "PC", primary: "#E60023", accent: "#FFFFFF" },
  { id: "duma", name: "Duma", short: "DUM", primary: "#0066FF", accent: "#FFFFFF" },
  { id: "puntor", name: "PuntoR", short: "PR", primary: "#00C853", accent: "#000000" },
  { id: "bullpadel", name: "Bullpadel", short: "BP", primary: "#1B1B1B", accent: "#FFEB00", logo: "https://customer-assets.emergentagent.com/job_padel-hub-12/artifacts/y5klalk6_IMG_9016.png" },
  { id: "babolat", name: "Babolat", short: "BAB", primary: "#FFEA00", accent: "#000000", logo: "https://customer-assets.emergentagent.com/job_padel-hub-12/artifacts/xt691eo7_IMG_9015.png" },
  { id: "wilson", name: "Wilson", short: "WIL", primary: "#E60023", accent: "#FFFFFF", logo: "https://customer-assets.emergentagent.com/job_padel-hub-12/artifacts/womertc2_IMG_9017.png" },
  { id: "head", name: "HEAD", short: "HEAD", primary: "#000000", accent: "#FFFFFF", logo: "https://customer-assets.emergentagent.com/job_padel-hub-12/artifacts/7vu0ozor_IMG_9014.jpeg" },
];

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Returns the avatar image URL for a user.
 * Priority:
 *   1. Uploaded photo (`photo_path`)
 *   2. DiceBear with chosen style + seed + background
 */
export function getAvatarUrl(user, size = 96) {
  if (!user) return null;
  if (user.photo_path) {
    return `${API_URL}/api/users/photo/${user.photo_path}`;
  }
  const style = user.avatar_style || "personas";
  const seed = encodeURIComponent(user.avatar_seed || user.name || user.id || "padel");
  const bg = user.avatar_bg || "ccff00";
  return `https://api.dicebear.com/9.x/${style}/svg?seed=${seed}&backgroundColor=${bg}&size=${size}`;
}

export function getInitials(name) {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export function getBrand(brandId) {
  const b = AVATAR_BRANDS.find((b) => b.id === brandId) || null;
  if (!b) return null;
  if (b.logo) return b;
  const logo = BRAND_LOGOS[b.id];
  return logo ? { ...b, logo } : b;
}

// ============================================================
// REAL-WORLD GEAR CATALOG (paddle, apparel, shoes, accessories)
// Used by Dashboard & ProfileEdit "Mi Equipamiento" section.
// ============================================================
export const PALA_BRANDS = [
  { id: "dyd", name: "DYD Padel", color: "#CCFF00", text: "#000" },
  { id: "bullpadel", name: "Bullpadel", color: "#FFEB00", text: "#000" },
  { id: "babolat", name: "Babolat", color: "#FFEA00", text: "#000" },
  { id: "wilson", name: "Wilson", color: "#E60023", text: "#FFF" },
  { id: "head", name: "HEAD", color: "#000", text: "#FFF" },
  { id: "nox", name: "NOX", color: "#00C853", text: "#000" },
  { id: "adidas", name: "Adidas", color: "#000", text: "#FFF" },
  { id: "siux", name: "Siux", color: "#FF6B00", text: "#000" },
  { id: "starvie", name: "StarVie", color: "#0066FF", text: "#FFF" },
  { id: "drop_shot", name: "Drop Shot", color: "#1B1B1B", text: "#FFEB00" },
  { id: "vibora", name: "Vibor-A", color: "#8B0000", text: "#FFF" },
  { id: "padel_revolution", name: "Padel Revolution", color: "#0EA5E9", text: "#FFF" },
];

export const APPAREL_BRANDS = [
  { id: "dyd", name: "DYD Padel", color: "#CCFF00", text: "#000" },
  { id: "club_premier", name: "Club Premier", color: "#0D1B4C", text: "#FFD700" },
  { id: "los_encinos", name: "Los Encinos", color: "#1B5E20", text: "#FFF" },
  { id: "padelville", name: "Padelville", color: "#7C4DFF", text: "#FFF" },
  { id: "padel_center", name: "Padel Center", color: "#E60023", text: "#FFF" },
  { id: "duma", name: "Duma", color: "#0066FF", text: "#FFF" },
  { id: "bullpadel", name: "Bullpadel", color: "#FFEB00", text: "#000" },
  { id: "adidas", name: "Adidas", color: "#000", text: "#FFF" },
  { id: "nike", name: "Nike", color: "#000", text: "#FFF" },
  { id: "puma", name: "Puma", color: "#000", text: "#FFEB00" },
  { id: "lotto", name: "Lotto", color: "#FFD700", text: "#000" },
  { id: "joma", name: "Joma", color: "#E60023", text: "#FFF" },
  { id: "wilson", name: "Wilson", color: "#E60023", text: "#FFF" },
  { id: "babolat", name: "Babolat", color: "#FFEA00", text: "#000" },
  { id: "head", name: "HEAD", color: "#000", text: "#FFF" },
  { id: "nox", name: "NOX", color: "#00C853", text: "#000" },
  { id: "asics", name: "Asics", color: "#0061AB", text: "#FFF" },
];

export const SHOES_BRANDS = [
  { id: "dyd", name: "DYD Padel", color: "#CCFF00", text: "#000" },
  { id: "munich", name: "MÚNICH", color: "#E63946", text: "#FFF" },
  { id: "asics", name: "Asics Gel-Padel", color: "#0061AB", text: "#FFF" },
  { id: "adidas", name: "Adidas Padel", color: "#000", text: "#FFF" },
  { id: "nike", name: "Nike Court", color: "#000", text: "#FFF" },
  { id: "babolat", name: "Babolat Jet", color: "#FFEA00", text: "#000" },
  { id: "head", name: "HEAD Sprint", color: "#000", text: "#FFF" },
  { id: "wilson", name: "Wilson Rush", color: "#E60023", text: "#FFF" },
  { id: "bullpadel", name: "Bullpadel Hack", color: "#FFEB00", text: "#000" },
  { id: "lotto", name: "Lotto Mirage", color: "#FFD700", text: "#000" },
  { id: "joma", name: "Joma Slam", color: "#E60023", text: "#FFF" },
  { id: "puma", name: "Puma Solarflash", color: "#000", text: "#FFEB00" },
  { id: "kelme", name: "Kelme", color: "#005FA8", text: "#FFF" },
];

function _withLogo(b) {
  if (!b) return null;
  if (b.logo) return b;
  const logo = BRAND_LOGOS[b.id];
  return logo ? { ...b, logo } : b;
}

export function getPalaBrand(id) { return _withLogo(PALA_BRANDS.find((b) => b.id === id) || null); }
export function getApparelBrand(id) { return _withLogo(APPAREL_BRANDS.find((b) => b.id === id) || null); }
export function getShoesBrand(id) { return _withLogo(SHOES_BRANDS.find((b) => b.id === id) || null); }

/**
 * Determine the medal-ring color tier from a list of top-badges.
 * Highest-points badges win: Leyenda > Maestro > Élite Local > etc.
 */
export function getMedalRing(topBadges) {
  if (!topBadges || topBadges.length === 0) return null;
  const codes = topBadges.map((b) => b.code);
  if (codes.includes("points_5000")) return { color: "#FFD700", glow: "rgba(255,215,0,0.6)", label: "Leyenda" };
  if (codes.includes("points_2500")) return { color: "#C0C0C0", glow: "rgba(192,192,192,0.6)", label: "Maestro" };
  if (codes.includes("points_1000")) return { color: "#CD7F32", glow: "rgba(205,127,50,0.6)", label: "Élite Local" };
  if (codes.includes("matches_100") || codes.includes("wins_100")) return { color: "#A855F7", glow: "rgba(168,85,247,0.6)", label: "Centurión" };
  return null;
}
